/**
 * Implements infinite scrolling functionality for a web component.
 *  This component loads additional content automatically as the user scrolls to the bottom of the page.
 *  It requires an element with class `.js-pagination__next-button` for fetching the next set of items.
 *
 * Usage:
 *  {%- paginate blog.articles by section.settings.items_per_page -%}
 *    <infinite-scroll data-infinite-scroll-enabled="{% if settings.pagination_style == 'infinite' %}true{% else %}false{% endif %}">
 *      <div class="js-pagination__item">One</div>
 *      <div class="js-pagination__item">Two</div>
 *      <div class="js-pagination__item">Three</div>
 *
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate %}
 *      {%- endif -%}
 *    </infinite-scroll>
 *  {%- endpaginate -%}
 */
if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // Cleans up resources when the element is removed from the DOM
    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.generation = (this.generation || 0) + 1;
      this.pagesLoaded = 1;
      this.fetchInProgress = false;
      this.restoring = false;
      this.exhausted = false;
      this.retryRequired = false;
      // Elements involved in pagination
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');
      if (typeof normalizeBrincatoysSearchPagination === 'function') normalizeBrincatoysSearchPagination(this);

      // Guard clause to ensure necessary elements exist
      if (!this.paginationContainer || !this.paginationItems.length || !this.nextButton?.hasAttribute('href')) {
        return;
      }

      this.bindListeners();
      if (this.querySelector('#product-grid')) {
        this.pageHideHandler = this.saveResultsState.bind(this);
        window.addEventListener('pagehide', this.pageHideHandler);
        // Facets reinitialise this element in the same document; only a fresh
        // document needs to rebuild the batches saved in its history entry.
        if (!this.historyRestoreAttempted) {
          this.historyRestoreAttempted = true;
          this.restoreResults();
        }
      }
    }

    // Removes event listeners and stops any ongoing intervals or observers
    destroy() {
      this.generation = (this.generation || 0) + 1;
      clearInterval(this.nextButtonTimer);
      clearTimeout(this.reinitTimer);
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      if (this.pageHideHandler) window.removeEventListener('pagehide', this.pageHideHandler);
    }

    saveResultsState() {
      if (this.restoring || !window.history?.replaceState) return;
      try {
        const {pathname, search} = window.location;
        window.history.replaceState({
          ...window.history.state,
          searchParams: search.slice(1),
          btProductResults: {url: pathname + search, pages: this.pagesLoaded, scrollY: window.scrollY}
        }, '', pathname + search);
      } catch (_) {
        // Normal navigation remains available if history state is unavailable.
      }
    }

    async restoreResults() {
      const saved = window.history?.state?.btProductResults;
      if (!saved || saved.url !== window.location.pathname + window.location.search ||
          !Number.isInteger(saved.pages) || saved.pages < 1 || saved.pages > 1000 ||
          !Number.isFinite(saved.scrollY) || saved.scrollY < 0) return;

      const generation = this.generation;
      this.restoring = true;
      let loaded = true;
      while (this.pagesLoaded < saved.pages && !this.exhausted) {
        loaded = await this.fetchNextItems({restore: true});
        if (!loaded || generation !== this.generation) break;
      }
      if (generation !== this.generation) return;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (generation !== this.generation) return;
      this.restoring = false;
      if (loaded) window.scrollTo({top: saved.scrollY, behavior: 'instant'});
      else this.nextButton?.scrollIntoView({block: 'center', behavior: 'instant'});
    }

    // Handles the "Next" button click to fetch the next set of items
    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems({manual: true});
    }

    // Sets up the infinite scroll functionality
    bindListeners() {
      // Setup click handler for the "Next" button
      this.clickHandler = this.onNextButtonClick.bind(this);
      this.nextButton.addEventListener('click', this.clickHandler);

      // Initialize IntersectionObserver if infinite scrolling is enabled
      if (this.dataset.infiniteScrollEnabled === 'true') {
        this.intersectionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.restoring) {
              // Load more results when the "Next" button is in view
              this.fetchNextItems();
              this.nextButtonTimer = setInterval(this.fetchNextItems.bind(this), 1500);
            } else {
              clearInterval(this.nextButtonTimer);
            }
          });
        }, {rootMargin: `${window.innerHeight}px 0px`});

        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    // Fetches the next set of items and updates the DOM
    async fetchNextItems({restore = false, manual = false} = {}) {
      if (this.fetchInProgress || (this.restoring && !restore) || this.exhausted ||
          (this.retryRequired && !manual) ||
          !this.nextButton?.hasAttribute('href')) return false;

      const generation = this.generation;
      const button = this.nextButton;
      const originalButtonText = button.textContent;
      this.retryRequired = false;
      const errorMessage = this.querySelector('.js-pagination__error');
      if (errorMessage) errorMessage.hidden = true;
      this.fetchInProgress = true;
      this.nextButton.setAttribute('aria-disabled', 'true');
      this.nextButton.textContent = window.strings.general.pagination.loadingText;

      try {
        if (typeof normalizeBrincatoysSearchPagination === 'function') normalizeBrincatoysSearchPagination(this);
        const response = await fetch(this.nextButton.href);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        if (generation !== this.generation) return false;

        // Append new items
        const newItemsHTML = Array.from(template.content.querySelectorAll('.js-pagination__item'))
          .map(item => item.outerHTML)
          .join('');
        if (!newItemsHTML) throw new Error('The next page contains no results.');
        this.paginationItems[this.paginationItems.length - 1].insertAdjacentHTML('afterend', newItemsHTML);
        const lastIndex = this.paginationItems.length
        this.paginationItems = this.querySelectorAll('.js-pagination__item');
        this.pagesLoaded++;

        this.reinitTimer = setTimeout(() => {
          if (generation !== this.generation) return;
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          window.stickersReinit?.();

          if (!restore && document.body.classList.contains('tab-active') && window.theme?.settings.paginationLoadType === 'show_more') {
            this.paginationItems[lastIndex]?.querySelector('a')?.focus();
          }
        }, 300);

        // Update pagination controls
        this.updatePaginationControls(template);
        return true;
      } catch (error) {
        if (generation === this.generation) {
          this.retryRequired = true;
          clearInterval(this.nextButtonTimer);
          if (errorMessage) errorMessage.hidden = false;
          console.error('Failed to load new items:', error);
        }
        return false;
      } finally {
        if (generation === this.generation) {
          this.fetchInProgress = false;
          button.removeAttribute('aria-disabled');
          button.textContent = originalButtonText;
        }
      }
    }

    // Updates pagination controls based on the fetched content
    updatePaginationControls(template) {
      if (typeof normalizeBrincatoysSearchPagination === 'function') normalizeBrincatoysSearchPagination(template.content);
      const nextPagination = template.content.querySelector('.js-pagination');
      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    // Refreshes the "Next" button event listener after content update
    refreshNextButton() {
      this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.nextButton.addEventListener('click', this.clickHandler);
    }

    // Removes pagination and related elements when no more pages are available
    cleanupAfterLastPage() {
      this.exhausted = true;
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      clearInterval(this.nextButtonTimer);
      this.paginationContainer.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
